/**
 * Eudor VAS Log Tracer (https://logs.eudorng.com) single sign-on, admin only.
 *
 * POST /admin/log-tracer/sso returns a one-minute, single-use sign-in link; the portal
 * opens it in a NEW TAB (the tracer is never embedded: it refuses frames). The body
 * is an optional deep link into the tracer's search: {network, kind, q, partner,
 * product, from, to}. Dates are Lagos days (YYYY-MM-DD); the tracer allows 7 days.
 */

export const SSO_PATH = '/admin/log-tracer/sso';
export const MAX_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Only an https/http link to the tracer's /sso is followed (never javascript:, data:, …). */
export const isHandoffUrl = (v) => {
  try {
    const u = new URL(v);
    return (u.protocol === 'https:' || u.protocol === 'http:') && u.pathname === '/sso' && u.searchParams.has('token');
  } catch (e) {
    return false;
  }
};

/** YYYY-MM-DD of a date in Lagos (the tracer's time zone). */
export const lagosDay = (d) => {
  const t = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Lagos', year: 'numeric', month: '2-digit', day: '2-digit' }).format(t);
};

/**
 * A range of Lagos days around an event: the day before to two days after (what
 * usually happens next: the telco notification, the partner webhook, the retry),
 * never past today. Unknown time: the last two days.
 */
export const rangeAround = (when, now = new Date()) => {
  const t = when ? new Date(when) : null;
  if (!t || Number.isNaN(t.getTime())) return { from: lagosDay(new Date(now.getTime() - DAY_MS)), to: lagosDay(now) };
  const end = Math.min(t.getTime() + 2 * DAY_MS, now.getTime());
  return { from: lagosDay(new Date(t.getTime() - DAY_MS)), to: lagosDay(new Date(Math.max(end, t.getTime()))) };
};

/** A from/to (YYYY-MM-DD) cut to the tracer's 7-day limit, keeping the end. */
export const clampRange = (from, to, now = new Date()) => {
  const end = to ? new Date(`${to}T12:00:00Z`) : now;
  let start = from ? new Date(`${from}T12:00:00Z`) : new Date(end.getTime() - DAY_MS);
  if (end.getTime() - start.getTime() > (MAX_DAYS - 1) * DAY_MS) start = new Date(end.getTime() - (MAX_DAYS - 1) * DAY_MS);
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
};

/** The last n Lagos days, today included (default the tracer's maximum, 7). */
export const recentRange = (days = MAX_DAYS, now = new Date()) => ({
  from: lagosDay(new Date(now.getTime() - (Math.min(days, MAX_DAYS) - 1) * DAY_MS)),
  to: lagosDay(now),
});

/** Host of a webhook URL ("https://api.partner.com/x" -> "api.partner.com"). */
export const hostOf = (url) => {
  if (!url) return '';
  try {
    return new URL(/^[a-z]+:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase();
  } catch (e) {
    return '';
  }
};

/** Deep links (bodies for POST /admin/log-tracer/sso). */
export const traceSearch = {
  phone: (phone, range = {}) => ({ network: 'vas', kind: 'phone', q: String(phone || '').trim(), ...range }),
  partner: (id) => ({ network: 'vas', kind: 'partner', q: String(id) }),
  product: (id) => ({ network: 'vas', kind: 'product', q: String(id) }),
  host: (host, range = {}) => ({ network: 'vas', kind: 'host', q: hostOf(host) || String(host || '').trim(), ...range }),
};
